/**
 * URL actions for the Inspector — Copy link + Open in a new tab (audit row 6).
 *
 * Port of the monolith's `__urlOk` / `__urlCopyDo` / the `_tail()` shortener
 * (index.html L12894, L12918–12937, L13542–13552).
 *
 * WHY THESE ARE PURE FUNCTIONS AND NOT A DOM POST-PASS. The monolith wires the
 * pair once with `__urlBarsWire(host)` — a single sweep over the rendered panel
 * that appends `.ech-ua` after every `[data-urlact]` control and inside every
 * `[data-urlbar]` row, then re-syncs the anchor's href on each keystroke. React
 * owns this DOM, so the equivalent is a `<UrlBar>` rendered next to the control
 * and the href re-syncing because the value is controlled state. The CONTRACT is
 * what has to match, and it does:
 *
 *   · the pair appears ONLY on fields the block DECLARED `type:"url"` — an
 *     image's hosted address is a reference, not a destination, so it gets
 *     nothing (`__urlActAttr` L12899 tests `f.type==="url"`, never the value)
 *   · Open is a real <a target="_blank" rel="noopener noreferrer">, never
 *     window.open — a popup call from inside the CloudPage frame gets blocked,
 *     an anchor click can't be
 *   · both actions go dim when the value isn't a usable link, and the field
 *     keeps its (dim) bar while empty rather than having buttons appear out of
 *     nowhere the moment a community resolves
 *   · Copy falls back to execCommand: the prototype runs on file://, where
 *     navigator.clipboard is unavailable
 */

/** monolith `__urlOk` (L12894) — verbatim regex. */
export function urlOk(v: unknown): boolean {
  return /^https?:\/\/[^\s]+\.[^\s]/i.test(String(v ?? "").trim());
}

/**
 * The shortened display form of a link: `host/…/filename` with the filename and
 * any query string BOLDED (monolith `_tail`, L13542–13552).
 *
 * The monolith returns an HTML string with a `<b>` in it and drops it through
 * innerHTML. React renders text, so this returns the two halves instead and the
 * component does `{text}<b>{bold}</b>` — same output, no dangerouslySetInnerHTML.
 */
export interface UrlTail { text: string; bold: string }

export function urlTail(u: unknown): UrlTail {
  const s = String(u ?? "");
  const m = /^https?:\/\/([^/]+)\/(.*)$/i.exec(s);
  if (!m) return { text: s.replace(/^https?:\/\//i, ""), bold: "" };
  const host = m[1].replace(/^www\./i, "");
  const rest = m[2];
  const last = rest.split("?")[0].split("/").filter(Boolean).pop() || "";
  const q = rest.indexOf("?") > -1 ? "?" + rest.split("?").slice(1).join("?") : "";
  if (!last) return { text: host + "/" + rest, bold: "" };
  const lead = rest.replace(/\?[\s\S]*$/, "").split("/").filter(Boolean).length > 1 ? "/…/" : "/";
  return { text: host + lead, bold: last + q };
}

/** The rendered text of a shortened link — what the empty-row filter measures. */
export const urlTailText = (u: unknown): string => {
  const t = urlTail(u);
  return (t.text + t.bold).trim();
};

/**
 * Copy to the clipboard, with the execCommand fallback (monolith `__urlCopyDo`).
 * Resolves true when something was copied. The caller flips its own label.
 */
export function copyLink(text: unknown): Promise<boolean> {
  const t = String(text ?? "").trim();
  if (!t) return Promise.resolve(false);
  const fallback = (): boolean => {
    try {
      const ta = document.createElement("textarea");
      ta.value = t;
      ta.style.cssText = "position:fixed;left:-9999px;top:0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      ta.remove();
      return !!ok;
    } catch { return false; }
  };
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      return navigator.clipboard.writeText(t).then(() => true, () => fallback());
    }
  } catch { /* fall through */ }
  return Promise.resolve(fallback());
}
